import InfiniteLoader from 'react-window-infinite-loader';
import React from 'react';
import { isSwipeRefreshEnabled } from 'mini-swipe-refresh/lib/utils/isSwipeRefreshEnabled';
import { LazySwipeDownManger } from 'mini-swipe-refresh';
import { useLayoutEffect, useRef } from 'react';
import { useLoadMoreItems } from '../hooks/useLoadMoreItems';
import { useScrollToTop } from '../hooks/useScrollToTop';
import { useWindowDimensions } from 'mini-hooks/lib/useWindowDimensions';
import { VariableSizeList as List } from 'react-window';
import type { VirtualizedListProps } from '../schema/VirtualizedListProps';

/* eslint-disable-next-line @typescript-eslint/no-explicit-any --
 * See aka.ms/mini-lint-errors
 *	> Unexpected any. Specify a different type. */
const VirtualizedList = (props: VirtualizedListProps<any>) => {
    const { fetchMoreItems, itemCount, containerHeight, enableSwipeRefresh } = props;

    const windowDimensions = useWindowDimensions();
    const loadMoreItems = useLoadMoreItems(fetchMoreItems);

    const computedContainerHeight =
        typeof containerHeight === 'function' ? containerHeight(windowDimensions) : containerHeight;

    const listRef = useRef(null);

    useLayoutEffect(() => {
        if (listRef?.current) {
            // Invalidate the itemSizeCache to trigger itemSize computation again.
            // We can optimize it instead of invalidating all the cache we can run
            // computation from specific index.
            listRef.current._listRef.resetAfterIndex(0);
        }
    }, [props.itemData]);

    useLayoutEffect(() => {
        const scrollableList = listRef?.current?._listRef;

        if (scrollableList && props.shouldScrollToBottom) {
            scrollableList.scrollToItem(itemCount - 1);
        }
        /* eslint-disable-next-line react-hooks/exhaustive-deps --
         * undefined
         *	> React Hook useLayoutEffect has a missing dependency: 'itemCount'. Either include it or remove the dependency array. */
    }, [props.shouldScrollToBottom]);

    useLayoutEffect(() => {
        if (listRef?.current && props.shouldScrollToTop) {
            listRef.current._listRef.scrollTo(0);
        }
    }, [props.shouldScrollToTop]);

    // When mail icon in module switcher is clicked and list should scroll to top
    useScrollToTop(() => {
        listRef?.current?._listRef?.scrollTo(0);
    });

    const canSwipeToRefresh = enableSwipeRefresh && isSwipeRefreshEnabled();

    return (
        <>
            <InfiniteLoader
                isItemLoaded={props.getIsItemLoaded}
                itemCount={itemCount}
                ref={listRef}
                loadMoreItems={loadMoreItems}
                threshold={props.preFetchThreshold}>
                {({ onItemsRendered, ref }) => (
                    <List
                        direction={props.isRTL ? 'rtl' : 'ltr'}
                        itemData={props.itemData}
                        height={computedContainerHeight}
                        useIsScrolling={props.enableIsScrolling}
                        itemCount={itemCount}
                        overscanCount={props.overscanCount}
                        itemSize={props.itemHeight}
                        itemKey={props.itemReactKey}
                        ref={ref}
                        onItemsRendered={onItemsRendered}
                        className={props.className}>
                        {props.itemRenderer}
                    </List>
                )}
            </InfiniteLoader>

            {/* swipe down to refresh manager */}
            {canSwipeToRefresh && (
                <LazySwipeDownManger
                    enabled={enableSwipeRefresh}
                    onRefresh={props.onRefreshListRequest}
                    scrollElement={listRef.current?._listRef?._outerRef}
                />
            )}
        </>
    );
};

VirtualizedList.defaultProps = {
    preFetchThreshold: 15,
    overscanCount: 2,
    isRTL: false,
    enableIsScrolling: false,
    shouldScrollToTop: false,
};

export default React.memo(VirtualizedList);
